import { defineStore } from "pinia";
import _ from "lodash";
import { BrowserRuleState, BrowserRuleAddRequest, BrowserRuleUpdateRequest } from "@/admin/types";
import { BrowserIdentifier, FetchState } from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import { useNotifierStore } from "@/shared/store/notifier";
import {
  listBrowserRules as fetchBrowserRules,
  addBrowserRule as addBrowserRuleApi,
  updateBrowserRule as updateBrowserRuleApi,
  deleteBrowserRule as deleteBrowserRuleApi
} from "@/shared/api/userService";

export const initialState: BrowserRuleState = {
  fetchState: FetchState.Uninitialized,
  browserRules: []
};

export const useBrowserRuleStore = defineStore("browserRule", {
  state: () => (_.cloneDeep(initialState)),
  getters: {
    isLoading: state => state.fetchState === FetchState.Loading,
    isLoaded: state => state.fetchState === FetchState.Loaded,
    isFailed: state => state.fetchState === FetchState.Failed
  },
  actions: {
    loadBrowserRules () {
      if (this.fetchState !== FetchState.Uninitialized) {
        return;
      }

      this.fetchState = FetchState.Loading;

      fetchBrowserRules()
        .then(response => {
          this.browserRules = response.data.browserRules;
          this.fetchState = FetchState.Loaded;
        })
        .catch(error => {
          this.fetchState = FetchState.Failed;
          return storeHelper.handleError({ error, rethrow: false });
        });
    },
    addBrowserRule (rule: BrowserRuleAddRequest) {
      return addBrowserRuleApi(rule)
        .then(response => {
          this.browserRules.push(response.data);
          const notifier = useNotifierStore();
          notifier.setSuccess("Browser successfully added.");
        })
        .catch(error => {
          return storeHelper.handleError({ error, rethrow: true });
        });
    },
    updateBrowserRule (args: { browser: BrowserIdentifier, rule: BrowserRuleUpdateRequest }) {
      return updateBrowserRuleApi(args.browser, args.rule)
        .then(response => {
          const index = this.browserRules.findIndex(rule => rule.browser === response.data.browser);
          if (index >= 0) {
            this.browserRules.splice(index, 1, response.data);
          }

          const notifier = useNotifierStore();
          notifier.setSuccess("Browser successfully updated.");
        })
        .catch(error => {
          return storeHelper.handleError({ error, rethrow: true });
        });
    },
    deleteBrowserRule (browser: BrowserIdentifier) {
      deleteBrowserRuleApi(browser)
        .then(() => {
          const index = this.browserRules.findIndex(rule => rule.browser === browser);
          if (index >= 0) {
            this.browserRules.splice(index, 1);
          }

          const notifier = useNotifierStore();
          notifier.setSuccess("Browser successfully removed.");
        })
        .catch(error => {
          return storeHelper.handleError({ error, rethrow: false });
        });
    }
  }
});
